/**
 * Hook para gestión de licencias
 */

import { useState, useEffect } from 'react'
import { onSnapshot, doc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { hasFeatureAccess, type UserLicense } from '@/lib/licenses'

const LICENSE_MUTATION_DISABLED_MESSAGE = 'La activación de licencias y trials desde el cliente está deshabilitada temporalmente por una actualización de seguridad'

export function useLicense(userId?: string) {
  const [license, setLicense] = useState<UserLicense | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Real-time license listener — updates instantly when Firestore doc changes
  useEffect(() => {
    if (!userId || !db) {
      setLicense(null)
      setIsLoading(false)
      return
    }

    const docRef = doc(db, 'user_licenses', userId)
    const unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        if (!snap.exists()) {
          setLicense(null)
          setIsLoading(false)
          return
        }

        const data = snap.data() as UserLicense

        if (data.expiresAt && data.expiresAt < Date.now() && data.status === 'active') {
          setLicense({ ...data, status: 'expired' })
        } else {
          setLicense(data)
        }

        setIsLoading(false)
      },
      (err) => {
        console.error('Error watching license:', err)
        setError(err.message)
        setIsLoading(false)
      }
    )

    return () => unsubscribe()
  }, [userId])

  // reload is a no-op — onSnapshot keeps state in sync automatically
  const loadLicense = () => {}

  // Activar licencia
  const activate = async (licenseCode: string) => {
    if (!userId) {
      return {
        success: false,
        message: 'Debes iniciar sesión para activar una licencia'
      }
    }

    void licenseCode
    setError(LICENSE_MUTATION_DISABLED_MESSAGE)
    return {
      success: false,
      message: LICENSE_MUTATION_DISABLED_MESSAGE
    }
  }

  // Activar prueba gratuita de 7 días
  const activateTrial = async () => {
    if (!userId) {
      return {
        success: false,
        message: 'Debes iniciar sesión para activar la prueba gratuita'
      }
    }

    setError(LICENSE_MUTATION_DISABLED_MESSAGE)
    return {
      success: false,
      message: LICENSE_MUTATION_DISABLED_MESSAGE
    }
  }

  // Verificar si tiene acceso a una feature
  const hasAccess = async (feature: keyof UserLicense['features']): Promise<boolean> => {
    if (!userId) return false

    try {
      return await hasFeatureAccess(userId, feature)
    } catch (error) {
      console.error('Error checking feature access:', error)
      return false
    }
  }

  // Helpers
  const isPremium = license?.type === 'premium' || license?.type === 'enterprise'
  const isActive = license?.status === 'active'
  const isExpired = license?.status === 'expired'
  const daysUntilExpiry = license?.expiresAt
    ? Math.max(0, Math.ceil((license.expiresAt - Date.now()) / (1000 * 60 * 60 * 24)))
    : null

  return {
    license,
    isLoading,
    error,
    activate,
    activateTrial,
    hasAccess,
    reload: loadLicense,

    // Computed properties
    licenseType: license?.type || 'free',
    isPremium,
    isActive,
    isExpired,
    daysUntilExpiry,
    isTrial: license?.type === 'free_trial',
    features: license?.features || {
      unlimitedHistory: false,
      telegramIntegration: false,
      customNotifications: false,
      prioritySupport: false
    }
  }
}
